import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MessageCircle, User, Mail, Lock, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { API_URL } from '../../constants';

const ChatRegisterPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ username:'', email:'', password:'', confirmPassword:'' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirmPassword) { setError('Passwords do not match.'); return; }
    if (form.password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/chat/register/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: form.username, email: form.email, password: form.password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.detail || 'Registration failed.');
      // Auto-login after register
      localStorage.setItem('chat_token', data.token);
      localStorage.setItem('chat_username', data.username);
      navigate('/chatroom');
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-emerald-700 text-white p-8 text-center">
          <MessageCircle className="w-16 h-16 mx-auto mb-3" />
          <h1 className="text-2xl font-bold">Join Global Chatroom</h1>
          <p className="text-emerald-200 text-sm mt-1">Connect with Gbongan community worldwide</p>
        </div>

        <div className="p-8">
          <Link to="/chatroom/login" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-emerald-600 mb-6">
            <ArrowLeft className="w-4 h-4" /> Already have an account? Sign in
          </Link>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm mb-5">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username *</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" required value={form.username} onChange={e => setForm(p=>({...p,username:e.target.value}))}
                  placeholder="Choose a unique username" maxLength={30}
                  className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent" />
              </div>
              <p className="text-xs text-gray-400 mt-1">This is how other chatters will see you</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="email" required value={form.email} onChange={e => setForm(p=>({...p,email:e.target.value}))}
                  placeholder="your@email.com"
                  className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type={showPw ? 'text' : 'password'} required value={form.password} onChange={e => setForm(p=>({...p,password:e.target.value}))}
                  placeholder="Min 6 characters"
                  className="w-full pl-9 pr-10 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500" />
                <button type="button" onClick={() => setShowPw(p=>!p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password *</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type={showPw ? 'text' : 'password'} required value={form.confirmPassword} onChange={e => setForm(p=>({...p,confirmPassword:e.target.value}))}
                  placeholder="Repeat password"
                  className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500" />
              </div>
            </div>

            <button type="submit" disabled={loading}
              className="w-full bg-emerald-700 text-white py-3 rounded-xl font-semibold hover:bg-emerald-800 transition disabled:opacity-60 mt-2">
              {loading ? 'Creating account…' : 'Create Account & Join Chat'}
            </button>
          </form>

          <p className="text-center text-xs text-gray-400 mt-5">
            By registering, you agree to be respectful and follow community guidelines.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChatRegisterPage;
